import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import React, { Children, forwardRef, useEffect, useState } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import {
  ProjectsRepository,
  type Projects as ProjectType,
} from "@repo/fetch-repository/projects";
import { Link } from "@repo/i18n/routing";
import { ArrowUpLeftIcon } from "lucide-react";
import { getLangDir } from "rtl-detect";
import { observer } from "mobx-react-lite";
import { useApp } from "@repo/app/useApp";
import { useLocale } from "next-intl";
import { useRouter } from "next/router";
import { cn } from "../libs/utils";
import { ArrowButton } from "../components/common/arrow-button";

export const ProjectGridDefinition: ComponentSettingsProps = {
  sideTitle: {
    type: "string",
    label: "عنوان کناری",
  },
  title: {
    type: "string",
    label: "عنوان",
    default: "پروژه ها",
  },
  description: {
    type: "string",
    label: "توضیحات",
    default:
      "لورم ایپسوم متن ساختگی با تولید سادگی نامفهوم از صنعت چاپ، و با استفاده از طراحان گرافیک است، چاپگرها و متون بلکه روزنامه و مجله در ستون و سطرآنچنان که لازم است، ",
  },
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface -- this component does not accept any props
export interface ProjectGridProps {
  children: React.ReactNode;
  sideTitle: string;
  title: string;
  description: string;
}

const ProjectGridComponent = forwardRef<HTMLDivElement, ProjectGridProps>(
  (props, ref) => {
    const app = useApp();
    const isEditor = app.rendererMode === "editor";
    const [projects, setProjects] = useState<ProjectType[]>([]);
    const router = useRouter();
    const locale = useLocale() || "en";
    const editorLocale = (router.query.locale as string) || "en";
    const dir = getLangDir(isEditor ? editorLocale : locale);
    useEffect(() => {
      const repository: ProjectsRepository = new ProjectsRepository();

      void repository
        .getMany({
          per_page: 4,
        })
        .then((res) => {
          if (res.succeeded && res.data) {
            setProjects(res.data);
          }
          // TODO: handle error
          // if (res.errors) {
          // }
        });
    }, []);
    return (
      <div
        className="w-full h-full grid grid-cols-12 gap-3 md:gap-10 lg:gap-16"
        ref={ref}
      >
        {props.sideTitle ? (
          <div className="absolute ltr:left-10 rtl:right-10 top-1/2 lg:flex hidden">
            <div className="size-full relative flex items-center justify-center">
              <div className="absolute rtl:rotate-90 ltr:-rotate-90 font-thin lg:text-3xl text-xl px-2 text-gray-300 before:content-[''] before:inline-block before:bg-gray-300 before:h-px before:lg:w-14 before:w-10 after:content-[''] after:inline-block after:bg-gray-300 after:h-px after:lg:w-14 after:w-10 flex items-center">
                <span className="mx-2 uppercase rtl:-mt-3 whitespace-nowrap rtl:font-doran rtl:font-medium">
                  {props.sideTitle}
                </span>
              </div>
            </div>
          </div>
        ) : null}
        <div className="relative col-span-12 lg:col-span-4 flex items-center justify-center">
          {Children.count(props.children) > 0 ? (
            props.children
          ) : (
            <div className="flex flex-col items-center justify-center">
              {/* <h1 className="font-helvetica text-[#ff770033] text-[190px] font-[100] rtl:font-bold rtl:font-dirooz leading-[1.07] tracking-[-8.56px] lg:text-[250px] lg:tracking-[-15.71px] text-center">
                03
              </h1> */}
              <p className="font-helvetica rtl:font-doran  text-[29px] lg:text-[48px] font-[100] rtl:font-bold leading-[1.07] text-[#6a6a6a] hover:text-[#f70]">
                {props.title || (ProjectGridDefinition.title.default as string)}
              </p>
              <p className="font-helvetica rtl:font-doran py-6  text-[18px] lg:text-[24px] font-[100] rtl:font-bold leading-normal px-7 text-[#6a6a6a] hover:text-[#f70] text-wrap break-words">
                {props.description ||
                  (ProjectGridDefinition.description.default as string)}
              </p>
            </div>
          )}
        </div>
        {projects.map((project, projectIndex) => {
          return (
            <div
              className={cn(
                projectIndex === 0
                  ? "col-span-7 lg:col-span-4"
                  : "col-span-5 last:col-span-7 lg:last:col-span-4 lg:col-span-4"
              )}
              key={project.id}
            >
              <img
                alt={project.name}
                className="w-full h-[100px] md:h-[300px] lg:h-[300px] object-cover"
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.png";
                }}
                src={(project.main_image.data as { url: string }).url}
              />
              <Link
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  if (isEditor) {
                    e.preventDefault();
                  }
                }}
                href={`/projects/${project.id}/${encodeURIComponent(
                  project.name.replace(/\s+/g, "-").toLowerCase()
                )}/`}
              >
                <div className="flex w-full justify-between items-center transition-colors hover:text-[#f70]">
                  <p className="py-3 rtl:font-doran text-[13px] md:text-[16px] truncate w-3/4">
                    {project.name}
                  </p>
                  <ArrowUpLeftIcon
                    className={cn(dir === "ltr" ? "scale-x-[-1]" : "")}
                  />
                </div>
              </Link>
            </div>
          );
        })}
        <Link
          href="/projects"
          className="col-span-7 lg:col-span-4 flex items-end justify-center pb-4 group"
        >
          <ArrowButton />
        </Link>
      </div>
    );
  }
);
ProjectGridComponent.displayName = "ProjectGrid";

export const ProjectGrid = observer(
  ProjectGridComponent
) as unknown as ComponentType<ProjectGridProps>;

export const ProjectGridSettings: ComponentSettings = {
  name: "EProjectGrid",
  props: ProjectGridDefinition,
  label: "گرید پروژه",
  childrenAllowed: true,
  widgets: {
    label: "گرید پروژه",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EProjectGrid", external: true }),
      }),
  },
};

ProjectGrid.componentSettings = ProjectGridSettings;
